import { unLinkTemplateDrawings } from "@/pages/templates/repositories/TemplatesRepositorie";
import { useTemplatesContext } from "@/pages/templates/context/templatesContext";
import type { GridApi } from "ag-grid-community";
import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";

export const useTemplateDrawingsActionsAndButtons = ({
	gridRef,
	searchTemplateDrawings,
	setRemoveDrawingError,
	TemplateId,
}: {
	TemplateId?: number | null;
	newMode?: boolean;
	gridRef: GridApi | null;
	searchTemplateDrawings: Function;
	setRemoveDrawingError: (err: string) => void | null;
	setShowLinkResourcesToAssetDialog?: Function;
	setListMessage: (err: string) => void | null;
}) => {
	const {
		state: { rowSelectedInDrawing, templateDrawingsList },
		actions: {
			setFirstTime,
			setRowSelectedInDrawing,
			setDrawingSearch,
			setDataDrawingSelected,
			setTemplateDrawingsList,
			setListDrawingsSelected,
		},
	} = useTemplatesContext();

	const [searchParams] = useSearchParams();
	const [refreshTrigger, setRefreshTrigger] = useState(1);
	const isMountRef = useRef(true);

	// BUTTONS STATES
	const [addButton, setAddButton] = useState<boolean>(true);
	const [deleteButton, setDeleteButton] = useState<boolean>(false);
	const [saveButton, setSaveButton] = useState<boolean>(false);
	const [discardButton, setDiscardButton] = useState<boolean>(false);
	const [deleteOpen, setDeleteOpen] = useState(false);

	const refresh = useCallback(() => {
		if (!isMountRef.current) {
			const updatedList = templateDrawingsList.filter(
				(item: any) => item.id !== -1,
			);

			setTemplateDrawingsList(updatedList);
			setFirstTime(true);
			setSaveButton(false);
			setDiscardButton(false);
			setAddButton(true);
			setDeleteButton(false);
			setDeleteOpen(false);
			setDrawingSearch("");
			setDataDrawingSelected(undefined);
			searchTemplateDrawings();
		}
	}, [searchParams, TemplateId]);

	const cancelAddDrawing = useCallback(() => {
		const updatedList = templateDrawingsList.filter(
			(item: any) => item.id !== -1,
		);

		setTemplateDrawingsList(updatedList);
		setDataDrawingSelected(null);
		setAddButton(true);
		setSaveButton(false);
		setDiscardButton(false);
		setDeleteButton(false);
		setDrawingSearch("");
	}, [
		templateDrawingsList,
		setTemplateDrawingsList,
		setDataDrawingSelected,
		setAddButton,
		setSaveButton,
		setDiscardButton,
		setDeleteButton,
		setDrawingSearch,
	]);

	const addRow = useCallback(() => {
		setAddButton(false);
		setSaveButton(true);
		setDiscardButton(true);
		setDeleteButton(false);
		const newData = { id: -1, TemplateId: TemplateId };
		const currentData = templateDrawingsList ? templateDrawingsList : [];
		const data = [newData, ...currentData];
		setTemplateDrawingsList(data);
	}, [templateDrawingsList, TemplateId]);

	const removeDrawings = useCallback(
		async (drawingsToRemove?: any[]) => {
			if (drawingsToRemove && drawingsToRemove.length > 0) {
				const formattedDrawings = drawingsToRemove.map((drawing: any) => ({
					DocumentName: drawing.DocumentName,
					IsAssetResource: drawing.IsAssetResource,
				}));

				const res = await unLinkTemplateDrawings(
					TemplateId as number,
					formattedDrawings,
				);
				if (res.ok) {
					refresh();
				} else {
					setRemoveDrawingError(res.message);
				}
			} else if (rowSelectedInDrawing) {
				const formattedDrawings = [
					{
						DocumentName: rowSelectedInDrawing.DocumentName,
						IsAssetResource: rowSelectedInDrawing.IsAssetResource,
					},
				];

				const res = await unLinkTemplateDrawings(
					TemplateId as number,
					formattedDrawings,
				);
				if (res.ok) {
					refresh();
				} else {
					setRemoveDrawingError(res.message);
				}
			}

			setRowSelectedInDrawing(null);
			setDeleteOpen(false);
			setAddButton(true);
			setSaveButton(false);
			setDiscardButton(false);
			setDeleteButton(false);
			setDrawingSearch("");
			setListDrawingsSelected([]);

			if (gridRef) {
				gridRef.deselectAll();
			}
		},
		[
			TemplateId,
			unLinkTemplateDrawings,
			refresh,
			setRemoveDrawingError,
			rowSelectedInDrawing,
			setRowSelectedInDrawing,
			setDeleteOpen,
			setAddButton,
			setSaveButton,
			setDiscardButton,
			setDeleteButton,
			setDrawingSearch,
			setListDrawingsSelected,
			gridRef,
		],
	);

	useEffect(() => {
		refresh();
	}, [refreshTrigger]);

	useEffect(() => {
		isMountRef.current = false;
	}, []);

	return {
		refresh,
		cancelAddDrawing,
		removeDrawings,
		addRow,
		addButton,
		deleteButton,
		saveButton,
		discardButton,
		deleteOpen,
		setSaveButton,
		setDiscardButton,
		setAddButton,
		setDeleteButton,
		setDeleteOpen,
	};
};
